import React from 'react'
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  Paper,
  Stack,
  Switch,
  Tab,
  Tabs,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material'
import { useOutletContext } from 'react-router-dom'
import type { AppOutletContext } from '../../app/AppShell'
import { useMe } from '../../app/MeContext'
import { usePageTitle } from '../../components/common/PageTitleContext'
import {
  approveStationRequest,
  createStationInvite,
  getFeatureDenies,
  getStationInvites,
  getStationMembers,
  getStationRequests,
  rejectStationRequest,
  setFeatureDeny,
  type FeatureDeny,
  type SeatSummary,
  type StationInvite,
  type StationMember,
  type StationRequest,
} from '../../api/stationMembers'
import {
  getStationConsolidations,
  triggerStationConsolidations,
  type ConsolidationEntry,
} from '../../api/consolidations'

type FeatureToggle = {
  key: string
  label: string
}

type NoticeState = {
  type: 'success' | 'error'
  message: string
}

const featureToggles: FeatureToggle[] = [
  { key: 'prep_sheet', label: 'Prep' },
  { key: 'show_clock', label: 'Show Clock' },
  { key: 'unified_inbox', label: 'Inbox' },
  { key: 'youtube_downloader', label: 'YouTube' },
]

const UserManagementPage: React.FC = () => {
  usePageTitle('User management')
  const ctx = useOutletContext<AppOutletContext>()
  const me = useMe()

  const stationId = ctx.activeStation?.id ?? me.stations?.[0]?.id ?? null
  const stationRole = stationId
    ? me.stations?.find((station) => station.id === stationId)?.role ?? ''
    : ''
  const isAdmin = stationRole === 'admin'

  const [loading, setLoading] = React.useState(false)
  const [error, setError] = React.useState<string | null>(null)
  const [members, setMembers] = React.useState<StationMember[]>([])
  const [requests, setRequests] = React.useState<StationRequest[]>([])
  const [invites, setInvites] = React.useState<StationInvite[]>([])
  const [denies, setDenies] = React.useState<FeatureDeny[]>([])
  const [consolidations, setConsolidations] = React.useState<ConsolidationEntry[]>([])
  const [seats, setSeats] = React.useState<SeatSummary | null>(null)
  const [subscriptionActive, setSubscriptionActive] = React.useState(false)
  const [inviteEmail, setInviteEmail] = React.useState('')
  const [inviteToken, setInviteToken] = React.useState<string | null>(null)
  const [inviteExpiry, setInviteExpiry] = React.useState<string | null>(null)
  const [savingDeny, setSavingDeny] = React.useState<Record<string, boolean>>({})
  const [requestAction, setRequestAction] = React.useState<Record<number, 'approve' | 'reject' | null>>({})
  const [consolidationNotice, setConsolidationNotice] = React.useState<NoticeState | null>(null)
  const [consolidationWorking, setConsolidationWorking] = React.useState(false)
  const [activeTab, setActiveTab] = React.useState<'overview' | 'requests' | 'members' | 'consolidations'>('overview')

  const denyLookup = React.useMemo(() => {
    const map = new Set<string>()
    for (const deny of denies) {
      map.add(`${deny.user_id}:${deny.feature_key}`)
    }
    return map
  }, [denies])

  const loadData = React.useCallback(async () => {
    if (!stationId) {
      return
    }

    setLoading(true)
    setError(null)

    try {
      const [membersRes, requestsRes, invitesRes, deniesRes] = await Promise.all([
        getStationMembers(stationId),
        getStationRequests(stationId),
        getStationInvites(stationId),
        getFeatureDenies(stationId),
      ])

      setMembers(membersRes.members ?? [])
      setSeats(membersRes.seats ?? null)
      const active = Boolean(membersRes.subscription_active)
      setSubscriptionActive(active)
      setRequests(requestsRes.requests ?? [])
      setInvites(invitesRes.invites ?? [])
      setDenies(deniesRes.denies ?? [])

      if (active) {
        const consolidationsRes = await getStationConsolidations(stationId)
        setConsolidations(consolidationsRes.consolidations ?? [])
      } else {
        setConsolidations([])
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unable to load user management data.')
    } finally {
      setLoading(false)
    }
  }, [stationId])

  React.useEffect(() => {
    setInviteToken(null)
    setInviteExpiry(null)
    setConsolidationNotice(null)

    if (!stationId || !isAdmin) {
      return
    }

    loadData()
  }, [stationId, isAdmin, loadData])

  const handleInviteSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault()

    if (!stationId) {
      return
    }

    setInviteToken(null)
    setInviteExpiry(null)
    setError(null)

    try {
      const response = await createStationInvite(stationId, inviteEmail.trim())
      setInviteToken(response.token)
      setInviteExpiry(response.expires_at ?? null)
      setInviteEmail('')
      await loadData()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unable to create invite.')
    }
  }

  const handleDenyToggle = async (member: StationMember, feature: string, nextAllow: boolean) => {
    if (!stationId) {
      return
    }

    const key = `${member.user_id}:${feature}`
    setSavingDeny((prev) => ({ ...prev, [key]: true }))

    try {
      const denied = !nextAllow
      await setFeatureDeny(stationId, member.user_id, feature, denied)
      setDenies((prev) => {
        if (denied) {
          if (prev.some((entry) => entry.user_id === member.user_id && entry.feature_key === feature)) {
            return prev
          }
          return [
            ...prev,
            {
              user_id: member.user_id,
              feature_key: feature,
              denied_by_user_id: me.user?.id ?? 0,
              denied_at: new Date().toISOString(),
              note: null,
            },
          ]
        }

        return prev.filter((entry) => !(entry.user_id === member.user_id && entry.feature_key === feature))
      })
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unable to update feature access.')
    } finally {
      setSavingDeny((prev) => ({ ...prev, [key]: false }))
    }
  }

  const handleRequestAction = async (requestId: number, action: 'approve' | 'reject') => {
    if (!stationId) {
      return
    }

    setRequestAction((prev) => ({ ...prev, [requestId]: action }))

    try {
      if (action === 'approve') {
        await approveStationRequest(stationId, requestId)
      } else {
        await rejectStationRequest(stationId, requestId)
      }

      await loadData()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unable to update request.')
    } finally {
      setRequestAction((prev) => ({ ...prev, [requestId]: null }))
    }
  }

  const handleConsolidationTrigger = async () => {
    if (!stationId) {
      return
    }

    setConsolidationWorking(true)
    setConsolidationNotice(null)
    setError(null)

    try {
      const response = await triggerStationConsolidations(stationId)
      setConsolidationNotice({
        type: 'success',
        message: `Sent ${response.created} consolidation invite${response.created === 1 ? '' : 's'}.`,
      })
      await loadData()
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Unable to trigger consolidation.'
      setConsolidationNotice({ type: 'error', message })
    } finally {
      setConsolidationWorking(false)
    }
  }

  if (!stationId) {
    return <Alert severity="info">Select a station to manage users.</Alert>
  }

  const showAdminSections = isAdmin && subscriptionActive
  const inboxSeats = seats?.inbox ?? null
  const prepSeats = seats?.prep ?? null
  const isFeatureSubscribed = (featureKey: string) => {
    if (featureKey === 'unified_inbox') {
      return (inboxSeats?.total ?? 0) > 0
    }
    if (featureKey === 'prep_sheet' || featureKey === 'show_clock') {
      return (prepSeats?.total ?? 0) > 0
    }
    return true
  }

  const handleTabChange = (_event: React.SyntheticEvent, value: string) => {
    if (value === 'requests' || value === 'members' || value === 'consolidations') {
      setActiveTab(value)
      return
    }

    setActiveTab('overview')
  }


  return (
    <Stack spacing={3}>
      <Stack spacing={1}>
        <Typography variant="h4" component="h1">
          User management
        </Typography>
        <Typography color="text.secondary">
          Approve requests, issue invite links, manage seats, and control feature access.
        </Typography>
      </Stack>

      {error ? <Alert severity="error">{error}</Alert> : null}
      {loading ? <CircularProgress size={24} /> : null}

      <Paper variant="outlined" sx={{ borderRadius: 2 }}>
        <Tabs
          value={activeTab}
          onChange={handleTabChange}
          variant="scrollable"
          scrollButtons="auto"
          aria-label="User management sections"
          sx={{ px: 2 }}
        >
          <Tab value="overview" label="Overview" />
          <Tab value="requests" label="Requests" disabled={!showAdminSections} />
          <Tab value="members" label="Members & access" disabled={!showAdminSections} />
          <Tab value="consolidations" label="Consolidations" disabled={!showAdminSections} />
        </Tabs>
      </Paper>

      {activeTab === 'overview' ? (
        <Stack spacing={3}>
          {isAdmin ? (
            <Paper variant="outlined" sx={{ borderRadius: 2 }}>
              <Stack spacing={2} sx={{ p: { xs: 2.5, md: 3 } }}>
                <Typography variant="h6">Seats</Typography>
                {subscriptionActive ? (
                  <Stack spacing={2}>
                    <Stack spacing={1}>
                      <Typography variant="subtitle2">Inbox seats</Typography>
                      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems="center">
                        <Chip label={`Total ${inboxSeats?.total ?? 0}`} />
                        <Chip label={`Assigned ${inboxSeats?.assigned ?? 0}`} />
                        <Chip label={`Available ${inboxSeats?.available ?? 0}`} color="success" />
                      </Stack>
                    </Stack>
                    <Stack spacing={1}>
                      <Typography variant="subtitle2">Prep seats</Typography>
                      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems="center">
                        <Chip label={`Total ${prepSeats?.total ?? 0}`} />
                        <Chip label={`Assigned ${prepSeats?.assigned ?? 0}`} />
                        <Chip label={`Available ${prepSeats?.available ?? 0}`} color="success" />
                      </Stack>
                    </Stack>
                    <Typography color="text.secondary">
                      Add seats in billing if you need more presenters.
                    </Typography>
                  </Stack>
                ) : (
                  <Alert severity="info">
                    This station is not subscribed. Invite links are available, but seat limits do not apply.
                  </Alert>
                )}
              </Stack>
            </Paper>
          ) : (
            <Alert severity="info">
              Only station admins can approve requests or manage feature access.
            </Alert>
          )}

          <Paper variant="outlined" sx={{ borderRadius: 2 }}>
            <Stack spacing={2} sx={{ p: { xs: 2.5, md: 3 } }}>
              <Typography variant="h6">Invite link</Typography>
              <Typography color="text.secondary">
                Generate a link to invite presenters. Subscribed stations expire links after 7 days.
              </Typography>
              <Box component="form" onSubmit={handleInviteSubmit}>
                <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems="center">
                  <TextField
                    label="Invitee email (optional)"
                    value={inviteEmail}
                    onChange={(event) => setInviteEmail(event.target.value)}
                    fullWidth
                  />
                  <Button type="submit" variant="contained">
                    Create invite
                  </Button>
                </Stack>
              </Box>
              {inviteToken ? (
                <Stack spacing={1}>
                  <Typography variant="subtitle2">Invite token</Typography>
                  <TextField value={inviteToken} InputProps={{ readOnly: true }} />
                  {inviteExpiry ? (
                    <Typography variant="body2" color="text.secondary">
                      Expires {new Date(inviteExpiry).toLocaleString()}
                    </Typography>
                  ) : null}
                </Stack>
              ) : null}
              {isAdmin ? (
                <Box>
                  <Typography variant="subtitle2" sx={{ mb: 1 }}>
                    Recent invites
                  </Typography>
                  {invites.length === 0 ? (
                    <Typography variant="body2" color="text.secondary">
                      No invites issued yet.
                    </Typography>
                  ) : (
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Email</TableCell>
                          <TableCell>Status</TableCell>
                          <TableCell>Expires</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {invites.map((invite) => (
                          <TableRow key={invite.id}>
                            <TableCell>{invite.email || 'N/A'}</TableCell>
                            <TableCell>{invite.status}</TableCell>
                            <TableCell>
                              {invite.expires_at ? new Date(invite.expires_at).toLocaleString() : 'N/A'}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  )}
                </Box>
              ) : null}
            </Stack>
          </Paper>
        </Stack>
      ) : null}

      {activeTab === 'requests' ? (
        showAdminSections ? (
          <Paper variant="outlined" sx={{ borderRadius: 2 }}>
            <Stack spacing={2} sx={{ p: { xs: 2.5, md: 3 } }}>
              <Typography variant="h6">Access requests</Typography>
              {requests.length === 0 ? (
                <Typography color="text.secondary">No pending requests.</Typography>
              ) : (
                <Stack spacing={1}>
                  {requests.map((request) => (
                    <Box key={request.id} sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      <Box sx={{ flex: 1 }}>
                        <Typography>{request.display_name}</Typography>
                        <Typography variant="body2" color="text.secondary">
                          {request.email}
                        </Typography>
                      </Box>
                      <Typography variant="body2" color="text.secondary">
                        {new Date(request.requested_at).toLocaleString()}
                      </Typography>
                      <Button
                        variant="contained"
                        size="small"
                        disabled={requestAction[request.id] !== null}
                        onClick={() => handleRequestAction(request.id, 'approve')}
                      >
                        Approve
                      </Button>
                      <Button
                        variant="outlined"
                        size="small"
                        disabled={requestAction[request.id] !== null}
                        onClick={() => handleRequestAction(request.id, 'reject')}
                      >
                        Reject
                      </Button>
                    </Box>
                  ))}
                </Stack>
              )}
            </Stack>
          </Paper>
        ) : (
          <Alert severity="info">
            Requests are available after the station is subscribed and an admin is assigned.
          </Alert>
        )
      ) : null}

      {activeTab === 'members' ? (
        showAdminSections ? (
          <Paper variant="outlined" sx={{ borderRadius: 2 }}>
            <Stack spacing={2} sx={{ p: { xs: 2.5, md: 3 } }}>
              <Typography variant="h6">Members & access</Typography>
              <Divider />
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Presenter</TableCell>
                    <TableCell>Email</TableCell>
                    {featureToggles.map((feature) => (
                      <TableCell key={feature.key} align="center">
                        {feature.label}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {members.map((member) => (
                    <TableRow key={member.user_id}>
                      <TableCell>
                        <Typography>{member.display_name}</Typography>
                        <Typography variant="body2" color="text.secondary">
                          {member.role} - {member.status}
                        </Typography>
                      </TableCell>
                      <TableCell>{member.email}</TableCell>
                      {featureToggles.map((feature) => {
                        const key = `${member.user_id}:${feature.key}`
                        const prepDenied = denyLookup.has(`${member.user_id}:prep_sheet`)
                        const denied = feature.key === 'show_clock'
                          ? prepDenied || denyLookup.has(key)
                          : denyLookup.has(key)
                        const busy = Boolean(savingDeny[key])
                        const subscribed = isFeatureSubscribed(feature.key)
                        return (
                          <TableCell key={feature.key} align="center">
                            <Switch
                              checked={!denied}
                              onChange={(_event, checked) => handleDenyToggle(member, feature.key, checked)}
                              disabled={busy || !subscribed || (feature.key === 'show_clock' && prepDenied)}
                              inputProps={{ 'aria-label': `${feature.label} access` }}
                            />
                          </TableCell>
                        )
                      })}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Stack>
          </Paper>
        ) : (
          <Alert severity="info">
            Members and access controls are available after the station is subscribed and an admin is assigned.
          </Alert>
        )
      ) : null}

      {activeTab === 'consolidations' ? (
        showAdminSections ? (
          <Paper variant="outlined" sx={{ borderRadius: 2 }}>
            <Stack spacing={2} sx={{ p: { xs: 2.5, md: 3 } }}>
              <Typography variant="h6">Subscription consolidation</Typography>
              <Typography color="text.secondary">
                Invite presenters with individual prep subscriptions to switch to the station plan. Their personal
                subscription will cancel at the end of the current billing period.
              </Typography>
              {consolidationNotice ? (
                <Alert severity={consolidationNotice.type}>{consolidationNotice.message}</Alert>
              ) : null}
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems="center">
                <Button
                  variant="contained"
                  onClick={handleConsolidationTrigger}
                  disabled={consolidationWorking}
                >
                  {consolidationWorking ? 'Sending...' : 'Send consolidation invites'}
                </Button>
                <Typography variant="body2" color="text.secondary">
                  This targets active individual prep subscriptions linked to this station.
                </Typography>
              </Stack>
              {consolidations.length === 0 ? (
                <Typography color="text.secondary">No consolidation invites have been sent.</Typography>
              ) : (
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Presenter</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell>Requested</TableCell>
                      <TableCell>Expires</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {consolidations.map((entry) => (
                      <TableRow key={entry.id}>
                        <TableCell>
                          <Typography>{entry.display_name}</Typography>
                          <Typography variant="body2" color="text.secondary">
                            {entry.email}
                          </Typography>
                        </TableCell>
                        <TableCell>{entry.status}</TableCell>
                        <TableCell>{new Date(entry.requested_at).toLocaleString()}</TableCell>
                        <TableCell>
                          {entry.expires_at ? new Date(entry.expires_at).toLocaleString() : 'N/A'}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </Stack>
          </Paper>
        ) : (
          <Alert severity="info">
            Consolidations are available after the station is subscribed and an admin is assigned.
          </Alert>
        )
      ) : null}
    </Stack>
  )

}

export default UserManagementPage
